/**
 * Tema de la app y fondo del lector.
 *
 * Son dos ajustes y no uno a proposito. En el lector el fondo no es
 * decoracion: es lo que rodea a la pagina cuando no llena la pantalla y lo
 * que se ve entre vinetas, y lo que se quiere ahi casi nunca coincide con lo
 * que se quiere en la biblioteca —la app en claro para buscar de dia y el
 * lector en negro siempre, por ejemplo—.
 *
 * Los colores viven en `estilos.css`, en bloques `[data-tema]` que redefinen
 * las mismas variables que `:root`. Aqui solo se decide cual se pone y se
 * recuerda. Lo que este pintado a mano en el CSS no cambia con el tema: si un
 * color tiene que cambiar, tiene que ser una variable.
 */

export type Tema = 'tinta' | 'papel' | 'medianoche' | 'auto'
export type Fondo = 'negro' | 'gris' | 'papel' | 'tema'

interface Opcion<T> {
  id: T
  nombre: string
  nota: string
  /** Cuadradito de color del boton, para reconocerlo sin leer */
  muestra: string
}

export const TEMAS: Opcion<Tema>[] = [
  {
    id: 'tinta',
    nombre: 'Tinta',
    nota: 'El de siempre: terracota sobre marron, sacado del logo.',
    muestra: 'linear-gradient(135deg, #14100e 50%, #cf6f3f 50%)',
  },
  {
    id: 'papel',
    nombre: 'Papel',
    nota: 'Claro, para buscar de dia o con sol de frente.',
    muestra: 'linear-gradient(135deg, #f4efe7 50%, #cf6f3f 50%)',
  },
  {
    id: 'medianoche',
    nombre: 'Medianoche',
    nota: 'Negro puro. En el OLED del iPhone los pixeles negros estan apagados: gasta menos bateria y a oscuras no deslumbra.',
    muestra: 'linear-gradient(135deg, #000000 50%, #cf6f3f 50%)',
  },
  {
    id: 'auto',
    nombre: 'Automatico',
    nota: 'Papel de dia y Tinta de noche, siguiendo al sistema.',
    muestra: 'linear-gradient(135deg, #f4efe7 50%, #14100e 50%)',
  },
]

export const FONDOS: Opcion<Fondo>[] = [
  {
    id: 'negro',
    nombre: 'Negro',
    nota: 'Lo mejor para manga: no compite con la pagina.',
    muestra: '#000000',
  },
  {
    id: 'gris',
    nombre: 'Gris',
    nota: 'Menos salto al pasar de una pagina oscura a los margenes.',
    muestra: '#1c1c1e',
  },
  {
    id: 'papel',
    nombre: 'Papel',
    nota: 'Margenes en hueso, como los de un tomo.',
    muestra: '#f2ece0',
  },
  {
    id: 'tema',
    nombre: 'El de la app',
    nota: 'El mismo fondo que el resto de la app, sea el que sea.',
    muestra: 'var(--fondo)',
  },
]

const CLAVE_TEMA = 'yomou.tema'
const CLAVE_FONDO = 'yomou.lector.fondo'

/** Color de la barra de estado por tema. Ver `aplicarTema`. */
const BARRA_ESTADO: Record<Exclude<Tema, 'auto'>, string> = {
  tinta: '#1A1614',
  papel: '#F4EFE7',
  medianoche: '#000000',
}

export function leerTema(): Tema {
  const guardado = localStorage.getItem(CLAVE_TEMA)
  return TEMAS.some((t) => t.id === guardado) ? (guardado as Tema) : 'tinta'
}

export function leerFondo(): Fondo {
  const guardado = localStorage.getItem(CLAVE_FONDO)
  return FONDOS.some((f) => f.id === guardado) ? (guardado as Fondo) : 'negro'
}

/**
 * El tema que se pinta de verdad.
 *
 * `auto` no existe en el CSS: se resuelve aqui mirando lo que pide el
 * sistema. Asi la paleta no hay que escribirla dos veces —una en su bloque y
 * otra dentro de un `@media`— y `theme-color`, que es JS y no CSS, puede ir
 * siempre a juego con lo que se esta viendo.
 */
function resolver(tema: Tema): Exclude<Tema, 'auto'> {
  if (tema !== 'auto') return tema
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'papel' : 'tinta'
}

export function aplicarTema(tema: Tema) {
  const real = resolver(tema)
  document.documentElement.dataset.tema = real
  /*
   * La franja de la barra de estado la pinta el navegador con `theme-color`,
   * no el CSS: sin tocarla, con el tema claro se quedaba del marron que trae
   * escrito el `index.html`.
   *
   * Ojo con el iPhone *instalado*: ahi manda ademas
   * `apple-mobile-web-app-status-bar-style`, que esta en `black-translucent`
   * porque es lo que deja pintar bajo la isla dinamica, y iOS decide el color
   * del reloj al arrancar. Sobre Papel puede quedarse blanco sobre claro.
   */
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BARRA_ESTADO[real])
  localStorage.setItem(CLAVE_TEMA, tema)
}

export function aplicarFondo(fondo: Fondo) {
  document.documentElement.dataset.fondo = fondo
  localStorage.setItem(CLAVE_FONDO, fondo)
}

/** Deja puesto lo guardado y, con `auto`, sigue al sistema si cambia solo. */
export function iniciarTema() {
  aplicarTema(leerTema())
  aplicarFondo(leerFondo())
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    if (leerTema() === 'auto') aplicarTema('auto')
  })
}
